import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bell, Plus, Trash2, Plane, Lock, Sparkles } from "lucide-react";
import Seo from "../components/Seo";
import AirportPicker from "../components/AirportPicker";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { CABINS, fmtPoints } from "../data/mock";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { log } from "../lib/log";

function AlertForm({ user, onCreate }) {
  const [form, setForm] = useState({ origin: "JFK", destination: "NRT", cabin: "business", maxPoints: 90000 });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <h2 className="text-lg font-bold text-slate-900">Create an alert</h2>
      <div className="mt-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <AirportPicker label="From" value={form.origin} onChange={(v) => set({ origin: v })} testId="alert-from-input" size="sm" />
          <AirportPicker label="To" value={form.destination} onChange={(v) => set({ destination: v })} testId="alert-to-input" size="sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Cabin</label>
          <div className="flex flex-wrap gap-2">
            {CABINS.map((c) => (
              <button key={c.key} onClick={() => set({ cabin: c.key })} className={`rounded-full border px-3.5 py-2 text-xs font-semibold ${form.cabin === c.key ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 text-slate-600"}`}>{c.label}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Max points: {fmtPoints(form.maxPoints)}</label>
          <input type="range" min={10000} max={200000} step={5000} value={form.maxPoints} onChange={(e) => set({ maxPoints: Number(e.target.value) })} className="w-full accent-blue-600" />
        </div>
        <Button onClick={() => onCreate(form)} disabled={!user || !form.origin || !form.destination} data-testid="create-alert-button" className="w-full gap-2 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4" /> Create alert</Button>
        {!user && (
          <p className="flex items-center justify-center gap-1.5 text-xs text-slate-500">
            <Lock className="h-3 w-3" /> <Link to="/login" className="font-semibold text-blue-600 hover:underline">Sign in</Link> to save alerts to your account
          </p>
        )}
      </div>
    </div>
  );
}

function AlertList({ alerts, onRemove }) {
  if (alerts.length === 0) {
    return (
      <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
        <Bell className="mx-auto h-10 w-10 text-slate-300" />
        <p className="mt-3 font-semibold text-slate-700">No alerts yet</p>
        <p className="text-sm text-slate-500">Create your first alert to get notified about open award seats.</p>
      </div>
    );
  }
  return (
    <div className="mt-4 space-y-3">
      {alerts.map((a) => (
        <div key={a.id} data-testid="alert-item" className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><Plane className="h-4 w-4" /></span>
            <div>
              <p className="text-sm font-bold text-slate-900">{a.origin} → {a.destination}</p>
              <p className="text-xs capitalize text-slate-500">{a.cabin} · under {fmtPoints(a.maxPoints)} pts</p>
            </div>
          </div>
          <button onClick={() => onRemove(a.id)} aria-label="Delete alert" data-testid="delete-alert-button" className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}

export default function Alerts() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    if (!user) { setAlerts([]); return; }
    api.listAlerts().then(setAlerts).catch((err) => log.error("Failed to load alerts", err));
  }, [user]);

  const create = async (form) => {
    try {
      const item = await api.createAlert(form);
      setAlerts((a) => [item, ...a]);
      toast({ title: "Alert created", description: `We'll notify you when ${form.origin} → ${form.destination} opens up.` });
    } catch (err) {
      log.error("Failed to create alert", err);
      toast({ title: "Could not create alert", description: "Please try again.", variant: "destructive" });
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteAlert(id);
      setAlerts((a) => a.filter((x) => x.id !== id));
    } catch (err) {
      log.error("Failed to delete alert", err);
      toast({ title: "Could not delete alert", variant: "destructive" });
    }
  };

  return (
    <main className="min-h-[70vh] bg-slate-50">
      <Seo title="Award availability alerts | FindMyFlight" description="Create alerts to be notified when the best award seats are released." path="/alerts" />
      <div className="mx-auto max-w-5xl px-5 py-10">
        <div className="flex items-center gap-2 text-blue-600"><Bell className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-wide">Alerts</span><span className="rounded-md bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white">PRO</span></div>
        <h1 className="mt-2 text-3xl font-extrabold text-slate-900">Continuous alerts</h1>
        <p className="mt-2 max-w-2xl text-slate-600">No more repetitive searches. Get notified the moment matching award seats are released.</p>
        {!user?.pro && (
          <Link to="/pricing" data-testid="alerts-pro-banner" className="mt-5 flex flex-col gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-900 transition-colors hover:bg-blue-100 sm:flex-row sm:items-center sm:justify-between">
            <span className="flex items-start gap-2"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" /><span><b>Alerts are a PRO feature.</b> You can set up alerts now, but notifications (in-app and email) are only delivered to PRO members.</span></span>
            <span className="shrink-0 font-bold">Go PRO — $8.99/mo →</span>
          </Link>
        )}
        <div className="mt-8 grid gap-8 lg:grid-cols-5">
          <div className="lg:col-span-2"><AlertForm user={user} onCreate={create} /></div>
          <div className="lg:col-span-3">
            <h2 className="text-lg font-bold text-slate-900">Your alerts</h2>
            <AlertList alerts={alerts} onRemove={remove} />
          </div>
        </div>
      </div>
    </main>
  );
}
